import { useEffect, useState } from "react";
import axios from "axios";
import { useSearchParams } from "react-router-dom";
import { Search, X } from "lucide-react";
import { Seo } from "@/components/Seo";
import { useLanguage } from "@/context/LanguageContext";
import { useSettings } from "@/context/SettingsContext";
import { Reveal } from "@/components/Reveal";
import { SectionHead } from "@/components/SectionHead";
import { VehicleCard } from "@/components/vehicles/VehicleCard";
import { VehicleDialog } from "@/components/vehicles/VehicleDialog";
import { PartCard, PartDialog } from "@/components/vehicles/PartCard";
import VehicleOfferForm from "@/components/vehicles/VehicleOfferForm";
import VehicleRequestForm from "@/components/vehicles/VehicleRequestForm";
import { vehicleTexts } from "@/components/vehicles/vehicleUtils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TABS = ["all", "sale", "rent", "part_sale", "part_wanted"];

const Vehicles = () => {
  const { t } = useLanguage();
  const { settings } = useSettings();
  const tv = t.vehicles;
  const vt = vehicleTexts(settings, tv);
  const [params, setParams] = useSearchParams();
  const tab = TABS.includes(params.get("type")) ? params.get("type") : "all";
  const [items, setItems] = useState([]);
  const [parts, setParts] = useState([]);
  const [open, setOpen] = useState(null);
  const [openPart, setOpenPart] = useState(null);
  const [query, setQuery] = useState("");
  const [cat, setCat] = useState("");
  useEffect(() => {
    axios.get(`${API}/vehicles`).then((r) => setItems(r.data)).catch(() => {});
    axios.get(`${API}/parts-market`).then((r) => setParts(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    const id = params.get("v");
    if (id && items.length) setOpen(items.find((x) => x.id === id) || null);
    const pid = params.get("p");
    if (pid && parts.length) setOpenPart(parts.find((x) => x.id === pid) || null);
  }, [params, items, parts]);

  const isPartTab = tab.startsWith("part_");
  const shown = tab === "all" ? items : items.filter((v) => v.kind === tab);
  const norm = (s) => (s || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  const q = norm(query.trim());
  const tabParts = parts.filter((p) => p.kind === tab);
  const shownParts = tabParts.filter((p) => (!cat || p.category === cat) && (!q || norm(`${p.name} ${p.compatible} ${p.description} ${tv.parts.category[p.category] || ""}`).includes(q)));
  const cats = Object.keys(tv.parts.category).filter((c) => tabParts.some((p) => p.category === c));
  const count = (k) => (k === "all" ? items.length : k.startsWith("part_") ? parts.filter((p) => p.kind === k).length : items.filter((v) => v.kind === k).length);
  const go = (k) => setParams(k === "all" ? {} : { type: k });
  const head = tab === "all" || tab === "sale" ? { overline: vt.overline, title: vt.title, accent: vt.accent, sub: vt.sub } : tv.heads[tab];
  const bottom = tab === "all" || tab === "sale" ? { overline: vt.sellOverline, title: vt.sellTitle, accent: vt.sellAccent, sub: vt.sellSub } : tv.request[tab];

  return (
    <main className="min-h-screen" data-testid="vehicles-page">
      <Seo page="vehicles" />
      <section className="relative overflow-hidden bg-mint/60 pb-16 pt-36">
        <div className="pointer-events-none absolute -right-24 top-10 h-[24rem] w-[24rem] rounded-full bg-cream blur-3xl" />
        <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
          <SectionHead key={tab} as="h1" overline={head.overline} title={head.title} accent={head.accent} sub={head.sub} />
          <div className="mt-10 flex flex-wrap gap-2" data-testid="vehicles-tabs">
            {TABS.map((k) => (
              <button key={k} type="button" onClick={() => go(k)} data-testid={`vehicles-tab-${k}`} className={`rounded-full px-5 py-2.5 font-mono text-xs uppercase tracking-[0.18em] transition-all ${tab === k ? "bg-forest text-white shadow-lg shadow-forest/20" : "border border-brand/30 text-brand hover:bg-white"}`}>
                {tv.tabs[k]} <span className="opacity-70">({count(k)})</span>
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16" data-testid={isPartTab ? "parts-list" : "vehicles-list"}>
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          {isPartTab && (
            <div className="mb-10 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between" data-testid="parts-filters">
              <div className="relative w-full lg:max-w-md">
                <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-brand/50" />
                <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder={tv.parts.searchPh} aria-label={tv.parts.search} data-testid="parts-search-input" className="w-full rounded-full border border-brand/20 bg-white py-3 pl-11 pr-11 text-sm text-forest placeholder:text-brand/40 outline-none transition-all focus:border-gold focus:ring-4 focus:ring-gold/10" />
                {query && <button type="button" onClick={() => setQuery("")} data-testid="parts-search-clear" aria-label="Effacer" className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full p-1 text-brand hover:bg-mint"><X className="h-4 w-4" /></button>}
              </div>
              <div className="flex flex-wrap gap-2" data-testid="parts-categories">
                <button type="button" onClick={() => setCat("")} data-testid="parts-cat-all" className={`rounded-full px-4 py-2 font-mono text-[11px] uppercase tracking-widest transition-all ${!cat ? "bg-gold text-white" : "border border-brand/30 text-brand hover:bg-mint"}`}>{tv.parts.allCategories}</button>
                {cats.map((c) => <button key={c} type="button" onClick={() => setCat(cat === c ? "" : c)} data-testid={`parts-cat-${c}`} className={`rounded-full px-4 py-2 font-mono text-[11px] uppercase tracking-widest transition-all ${cat === c ? "bg-gold text-white" : "border border-brand/30 text-brand hover:bg-mint"}`}>{tv.parts.category[c]}</button>)}
              </div>
            </div>
          )}
          {isPartTab ? (
            shownParts.length === 0 ? <Reveal><p className="rounded-3xl bg-mint p-8 text-base text-brand-hover" data-testid="parts-empty">{tabParts.length ? tv.parts.noResult : tab === "part_wanted" ? tv.parts.emptyWanted : tv.parts.empty}</p></Reveal> : (
              <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {shownParts.map((p, i) => <Reveal key={p.id} delay={0.06 * (i % 3)}><PartCard p={p} index={i} onOpen={setOpenPart} /></Reveal>)}
              </div>
            )
          ) : shown.length === 0 ? (
            <Reveal><p className="rounded-3xl bg-mint p-8 text-base text-brand-hover" data-testid="vehicles-empty">{vt.empty}</p></Reveal>
          ) : (
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {shown.map((v, i) => <Reveal key={v.id} delay={0.06 * (i % 3)}><VehicleCard v={v} index={i} onOpen={setOpen} /></Reveal>)}
            </div>
          )}
        </div>
      </section>

      <section id="vendre" className="bg-cream/60 py-20 scroll-mt-28" data-testid={`vehicle-bottom-${tab}`}>
        <div className="mx-auto grid max-w-7xl gap-12 px-5 sm:px-8 lg:grid-cols-5">
          <div className="lg:col-span-2"><SectionHead key={tab} overline={bottom.overline} title={bottom.title} accent={bottom.accent} sub={bottom.sub} /></div>
          <div className="lg:col-span-3">{tab === "all" || tab === "sale" ? <VehicleOfferForm /> : <VehicleRequestForm key={tab} kind={tab} />}</div>
        </div>
      </section>

      <VehicleDialog vehicle={open} onClose={() => { setOpen(null); if (params.get("v")) go(tab); }} />
      <PartDialog part={openPart} onClose={() => { setOpenPart(null); if (params.get("p")) go(tab); }} />
    </main>
  );
};

export default Vehicles;
